import { useCallback, useEffect, useMemo, useState } from 'react';
import { audits, type AuditableLocation } from '../api/audits';
import { newestPublished, templates, type FormTemplateSummary } from '../api/templates';
import {
  planCompliance,
  plans,
  type Auditor,
  type Plan,
  type PlanCompliance,
  type PlanRefusal,
  type PlanStatus,
} from '../api/plans';
import { Permission } from '../auth/permissions';
import { allowed, session } from '../auth/session';
import { IconCalendarOff, IconPencil, IconPlus, IconRefreshCw, IconTriangleAlert, IconX } from '../icons';
import { fixed, formatDate, translateError } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Field, Select, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { Table, type Column } from '../ui/Table';

/**
 * Denetim Planı — who is going where, and by when.
 *
 * <p>Built around the bulk case because that is the only case: nobody plans one store.
 * A quality manager picks a checklist, a month and an auditor, ticks a region's worth of
 * stores and raises thirty at once.</p>
 *
 * <p>Stores that cannot be planned are <b>reported, not skipped</b>. "Twenty-eight of
 * thirty" with no explanation is worse than a refusal: the two that failed are exactly the
 * ones somebody needs to know about, and silence makes them look planned.</p>
 */

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** A plain four-digit-year ISO date; anything else is not one the server will take. */
export const isoDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

/** Whether a plan's window touches the period. ISO dates compare as text. */
export function inPeriod(plan: { from: string; to: string }, period: { from: string; to: string }): boolean {
  return plan.from <= period.to && plan.to >= period.from;
}

/** A whole month, counted from today: 0 is this one, 1 the next. */
function month(offset: number): { from: string; to: string } {
  const now = new Date();

  return {
    from: iso(new Date(now.getFullYear(), now.getMonth() + offset, 1)),
    to: iso(new Date(now.getFullYear(), now.getMonth() + offset + 1, 0)),
  };
}

/** The month that is starting, which is what a plan is almost always about. */
const nextMonth = () => month(1);

/** The month that is ending, which is what a compliance question usually is. */
const thisMonth = () => month(0);

const STATES: PlanStatus[] = ['Planned', 'Started', 'Completed', 'Missed', 'Cancelled'];

export function AuditPlans() {
  const t = useTranslate();

  // The menu only offers this screen to somebody who may plan, but a route is not a door —
  // the address can be typed, and a reader of audits who lands here can still see the list.
  // They get the list without the two controls that would answer 403.
  const mayPlan = allowed(session(), Permission.ManagePlans);

  const [rows, setRows] = useState<Plan[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<PlanStatus | ''>('');
  const [compliance, setCompliance] = useState<PlanCompliance | null>(null);

  // Which period the summary is about. Its own control, because the answer is only ever
  // about one: the panel used to read this month and nothing else, while the form below it
  // proposes next month — so the period somebody had just planned was the one period they
  // could not look at, and with no plans this month the panel was not even on the screen.
  const [over, setOver] = useState(thisMonth);

  // The list follows the same period by default. It used to list every plan ever made under
  // a summary saying "no plans in this period" (field test, 2 November).
  const [onlyPeriod, setOnlyPeriod] = useState(true);

  // --- what the form needs to offer
  const [stores, setStores] = useState<AuditableLocation[]>([]);
  const [forms, setForms] = useState<FormTemplateSummary[]>([]);
  const [people, setPeople] = useState<Auditor[]>([]);
  const [listsReady, setListsReady] = useState(false);

  // --- the form
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(nextMonth);
  const [formId, setFormId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<PlanRefusal[]>([]);
  const [created, setCreated] = useState(0);
  const [cancelling, setCancelling] = useState<Plan | null>(null);
  const [editing, setEditing] = useState<{
    plan: Plan;
    from: string;
    to: string;
    assignee: string;
    note: string;
    notify: boolean;
  } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // A refusal belongs to what was tried. Left up after the form changed, a second refusal
  // looked like the first, and an accepted change could not be told from a refused one.
  useEffect(() => setEditError(null), [editing?.from, editing?.to, editing?.assignee, editing?.note]);
  const [reason, setReason] = useState('');

  const load = useCallback(async (state: PlanStatus | '') => {
    setLoading(true);
    try {
      const list = await plans.list(state ? { status: state } : {});
      setRows(list.rows);
      setTruncated(list.truncated);
      setError(null);
    } catch (e) {
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(status);
  }, [load, status]);

  // Over whichever period is being asked about — this month to begin with, because that is
  // usually the question. Reloaded with the list so raising or cancelling a plan moves the
  // numbers at once.
  useEffect(() => {
    void (async () => {
      try {
        setCompliance(await planCompliance(over.from, over.to));
      } catch {
        // The list is the screen. A summary that cannot be fetched leaves it alone.
      }
    })();
  }, [rows, over.from, over.to]);

  // The three lists the form offers, fetched once. Published templates only: planning
  // against a draft would plan a visit nobody can open.
  useEffect(() => {
    // Only the form uses them, and without the permission there is no form.
    if (!mayPlan) {
      setListsReady(true);
      return;
    }

    void (async () => {
      // Settled rather than awaited together. Promise.all rejects on the first failure and
      // throws away the answers that arrived, so one forbidden call used to empty all three
      // boxes — and the screen then blamed the two that had worked.
      const [where, what, who] = await Promise.allSettled([
        audits.locations(),
        templates.list(),
        plans.auditors(),
      ]);

      if (where.status === 'fulfilled') setStores(where.value);
      if (what.status === 'fulfilled') setForms(newestPublished(what.value));
      if (who.status === 'fulfilled') setPeople(who.value);

      setListsReady(true);
    })();
  }, [mayPlan]);

  const shown = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('tr');
    if (!needle) return stores;

    return stores.filter(
      (s) =>
        s.code.toLocaleLowerCase('tr').includes(needle) ||
        s.name.toLocaleLowerCase('tr').includes(needle),
    );
  }, [stores, search]);

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const result = await plans.create({
        locationIds: [...chosen],
        formId,
        assignedToUserId: assignee,
        from: period.from,
        to: period.to,
        note: note.trim() || undefined,
        notify,
      });

      setRefused(result.refused);
      setCreated(result.created.length);
      setError(null);

      // What went in leaves the selection. The button used to keep counting it, and a
      // second press planned those stores twice (field test, 2 November).
      setChosen(new Set(result.refused.map((r) => r.locationId)));

      // Only close when everything went in. A form that closed over two refusals would be
      // a form that hid them.
      if (result.refused.length === 0) {
        setOpen(false);
        setChosen(new Set());
        setNote('');
      }

      await load(status);
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // A dialog of our own rather than window.prompt, which froze the page and looked like
  // nothing else in the product (field test, 2 November).
  const cancel = (plan: Plan) => {
    setReason('');
    setCancelling(plan);
  };

  // A plan could only be cancelled and raised again; the server could change one all along,
  // the screen never asked it to (field test, 2 November).
  const edit = (plan: Plan) => {
    setEditError(null);
    setEditing({ plan, from: plan.from, to: plan.to, assignee: plan.assignedToUserId, note: plan.note ?? '', notify: true });
  };

  const confirmEdit = async () => {
    if (!editing) return;
    const { plan } = editing;
    try {
      await plans.change(plan.id, {
        from: editing.from,
        to: editing.to,
        assignedToUserId: editing.assignee,
        note: editing.note,
        notify: editing.notify,
      });
      await load(status);
    } catch (e) {
      // Kept open with the reason, rather than closed over a change that did not happen.
      setEditError(describeError(e, 'error.saveFailed'));
      throw e;
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    try {
      await plans.cancel(cancelling.id, reason.trim());
      await load(status);
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    }
  };

  const columns: Column<Plan>[] = [
    {
      id: 'location',
      header: t('plans.location'),
      className: 'is-primary',
      cell: (p) => `${p.locationCode} · ${p.locationName}`,
    },
    { id: 'form', header: t('plans.form'), cell: (p) => p.formName, className: 'is-muted' },
    { id: 'who', header: t('plans.assignedTo'), cell: (p) => p.assignedToName },
    {
      id: 'period',
      header: t('plans.period'),
      cell: (p) =>
        p.from === p.to ? formatDate(p.from) : `${formatDate(p.from)} – ${formatDate(p.to)}`,
    },
    {
      id: 'status',
      header: t('plans.status'),
      cell: (p) => <Status plan={p} />,
    },
  ];

  const ready = chosen.size > 0 && formId && assignee && period.from && period.to;

  return (
    <>
      <PageHeader
        crumbs={[t('app.reports')]}
        title={t('plans.title')}
        subtitle={t('plans.subtitle')}
        action={
          mayPlan ? (
            <Button onClick={() => setOpen((o) => !o)}>
              <IconPlus />
              {t('plans.new')}
            </Button>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {compliance ? (
        <Compliance report={compliance} over={over} onPeriod={setOver} />
      ) : null}

      {open ? (
        <Card className="nv-planform">
          <h3>{t('plans.new')}</h3>

          {listsReady && forms.length === 0 ? (
            <Alert tone="warning">{t('plans.noPublishedForm')}</Alert>
          ) : null}

          <div className="nv-planform__row">
            <Field label={t('plans.form')} hint={t('plans.formHint')}>
              {({ id }) => (
                <Select id={id} value={formId} onChange={(e) => setFormId(e.target.value)}>
                  <option value="">{t('plans.choose')}</option>
                  {forms.map((f) => (
                    <option key={f.id} value={f.id}>
                      {`${f.name} · v${f.version}`}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('plans.assignedTo')}>
              {({ id }) => (
                <Select id={id} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  <option value="">{t('plans.choose')}</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('plans.from')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  type="date"
                  value={period.from}
                  onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                />
              )}
            </Field>

            <Field label={t('plans.to')} hint={t('plans.periodHint')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  type="date"
                  value={period.to}
                  onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                />
              )}
            </Field>
          </div>

          <Field label={t('plans.note')} hint={t('plans.noteHint')}>
            {({ id }) => (
              <TextInput id={id} value={note} onChange={(e) => setNote(e.target.value)} />
            )}
          </Field>

          <div className="nv-planform__stores">
            <div className="nv-planform__storehead">
              <strong>{t('plans.stores', { count: String(chosen.size) })}</strong>

              <TextInput
                value={search}
                placeholder={t('plans.search')}
                onChange={(e) => setSearch(e.target.value)}
              />

              <Button
                variant="secondary"
                onClick={() => setChosen(new Set(shown.map((s) => s.id)))}
                disabled={shown.length === 0}
              >
                {t('plans.selectShown')}
              </Button>

              <Button variant="secondary" onClick={() => setChosen(new Set())} disabled={chosen.size === 0}>
                {t('plans.clear')}
              </Button>
            </div>

            <div className="nv-planform__list">
              {shown.map((s) => (
                <label key={s.id} className={chosen.has(s.id) ? 'is-chosen' : undefined}>
                  <input type="checkbox" checked={chosen.has(s.id)} onChange={() => toggle(s.id)} />
                  <span>
                    <b>{s.code}</b> {s.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <Toggle checked={notify} onCheckedChange={setNotify} label={t('plans.notify')} />

          <div className="nv-planform__foot">
            <Button onClick={() => void save()} disabled={!ready || saving}>
              {t('plans.create', { count: String(chosen.size) })}
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>

          {created > 0 ? <Badge tone="comment">{t('plans.created', { count: String(created) })}</Badge> : null}

          {refused.length > 0 ? (
            <div className="nv-planform__refused">
              <p>
                <IconTriangleAlert />
                {t('plans.refused', { count: String(refused.length) })}
              </p>
              <ul>
                {refused.map((r) => (
                  <li key={r.locationId}>
                    <b>{r.locationCode || '—'}</b> {r.locationName}
                    <em>{translateError(r.code, r.code)}</em>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
      ) : null}

      <ConfirmDialog
        open={cancelling !== null}
        title={t('plans.cancelTitle', { location: cancelling ? `${cancelling.locationCode} · ${cancelling.locationName}` : '' })}
        confirmLabel={t('plans.cancelConfirm')}
        confirmDisabled={reason.trim().length === 0}
        onConfirm={confirmCancel}
        onClose={() => setCancelling(null)}
      >
        <Field label={t('plans.cancelReason')}>
          {({ id }) => <Textarea id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={editing !== null}
        tone="primary"
        title={t('plans.editTitle', { location: editing ? `${editing.plan.locationCode} · ${editing.plan.locationName}` : '' })}
        confirmLabel={t('common.save')}
        confirmDisabled={!editing || !isoDate(editing.from) || !isoDate(editing.to) || editing.to < editing.from || !editing.assignee}
        onConfirm={confirmEdit}
        onClose={() => setEditing(null)}
      >
        {editing ? (
          <div className="nv-stack">
            <p className="nv-hint">{editing.plan.formName}</p>

            {/* Under way, the audit is the auditor's: person and start stay, only the end moves. */}
            {editing.plan.status === 'Started' ? <Alert tone="warning">{t('plans.editStarted')}</Alert> : null}

            <div className="nv-form-pair">
              <Field label={t('plans.from')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="date"
                    value={editing.from}
                    disabled={editing.plan.status === 'Started'}
                    onChange={(e) => setEditing({ ...editing, from: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t('plans.to')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="date"
                    value={editing.to}
                    min={editing.plan.status === 'Started' ? editing.plan.to : editing.from}
                    onChange={(e) => setEditing({ ...editing, to: e.target.value })}
                  />
                )}
              </Field>
            </div>

            <Field label={t('plans.assignedTo')}>
              {({ id }) => (
                <Select
                  id={id}
                  value={editing.assignee}
                  disabled={editing.plan.status === 'Started'}
                  onChange={(e) => setEditing({ ...editing, assignee: e.target.value })}
                >
                  {people.some((x) => x.id === editing.plan.assignedToUserId) ? null : (
                    <option value={editing.plan.assignedToUserId}>{editing.plan.assignedToName}</option>
                  )}
                  {people.map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('plans.note')}>
              {({ id }) => (
                <Textarea id={id} value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} />
              )}
            </Field>

            {editing.assignee !== editing.plan.assignedToUserId ? (
              <Toggle
                checked={editing.notify}
                onCheckedChange={(notify) => setEditing({ ...editing, notify })}
                label={t('plans.notifyNew')}
              />
            ) : null}

            {editError ? <Alert>{editError}</Alert> : null}
          </div>
        ) : null}
      </ConfirmDialog>

      <Card>
        <div className="nv-planfilter">
          <Field label={t('plans.status')}>
            {({ id }) => (
              <Select
                id={id}
                value={status}
                onChange={(e) => setStatus(e.target.value as PlanStatus | '')}
              >
                <option value="">{t('visits.all')}</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {t(`plans.status.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Toggle checked={onlyPeriod} onCheckedChange={setOnlyPeriod} label={t('plans.onlyPeriod')} />

          <Button variant="secondary" onClick={() => void load(status)} disabled={loading}>
            <IconRefreshCw className={loading ? 'nv-spin' : undefined} />
            {t('map.refresh')}
          </Button>
        </div>

        <Table
          caption={t('plans.title')}
          columns={columns}
          rows={onlyPeriod ? rows.filter((p) => inPeriod(p, over)) : rows}
          getRowId={(p) => p.id}
          status={loading ? 'loading' : 'ready'}
          emptyState={
            onlyPeriod && rows.length > 0 ? (
              // The period is empty, the screen is not: say where the plans are.
              <EmptyState
                icon={IconCalendarOff}
                title={t('plans.emptyPeriod')}
                text={t('plans.emptyPeriodText', { count: String(rows.length) })}
                action={<Button variant="secondary" onClick={() => setOnlyPeriod(false)}>{t('plans.showAllPeriods')}</Button>}
              />
            ) : (
              <EmptyState
                icon={IconCalendarOff}
                title={t('plans.empty')}
                text={t('plans.emptyText')}
              />
            )
          }
          rowActions={(p) =>
            mayPlan && (p.status === 'Planned' || p.status === 'Started') ? (
              <>
                <IconButton label={t('plans.editPlan')} icon={IconPencil} onClick={() => edit(p)} />
                <IconButton label={t('plans.cancelPlan')} icon={IconX} onClick={() => void cancel(p)} />
              </>
            ) : null
          }
        />

        {truncated ? <p className="nv-muted">{t('plans.truncated')}</p> : null}
      </Card>
    </>
  );
}

function Status({ plan }: { plan: Plan }) {
  const t = useTranslate();

  if (plan.isOverdue) {
    return (
      <Badge tone="critical" icon={IconTriangleAlert} title={plan.cancelledReason ?? undefined}>
        {t('tasks.overdue')}
      </Badge>
    );
  }

  const tone =
    plan.status === 'Completed'
      ? 'boolean'
      : plan.status === 'Missed'
        ? 'critical'
        : plan.status === 'Cancelled'
          ? 'na'
          : plan.status === 'Started'
            ? 'comment'
            : 'neutral';

  return (
    <Badge tone={tone} title={plan.cancelledReason ?? undefined}>
      {t(`plans.status.${plan.status}`)}
    </Badge>
  );
}

/**
 * Planned against done, this month.
 *
 * <p>The rate leaves cancelled plans out of the denominator: a visit called off because the
 * store was being refitted is not a visit somebody failed to make, and counting it as one
 * turns the figure into something nobody trusts.</p>
 */
function Compliance({
  report,
  over,
  onPeriod,
}: {
  report: PlanCompliance;
  over: { from: string; to: string };
  onPeriod: (period: { from: string; to: string }) => void;
}) {
  const t = useTranslate();
  const { total } = report;

  return (
    <>
      <Card className="nv-tally">
        <div className="nv-planform__row">
          <Field label={t('plans.tally.from')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="date"
                value={over.from}
                onChange={(e) => onPeriod({ ...over, from: e.target.value })}
              />
            )}
          </Field>

          {/* The hint belongs to the pair, so it hangs off the second one — the same way
              the plan form below does it, rather than wedged between the two boxes. */}
          <Field label={t('plans.tally.to')} hint={t('plans.tally.periodHint')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="date"
                value={over.to}
                onChange={(e) => onPeriod({ ...over, to: e.target.value })}
              />
            )}
          </Field>
        </div>

        {/* Said rather than left blank: an empty month and a broken screen look the same. */}
        {total.planned === 0 ? <p className="nv-muted">{t('plans.tally.none')}</p> : null}
      </Card>

      <div className="nv-mapstats">
        <Stat label={t('plans.tally.planned')} value={String(total.planned)} />
        <Stat label={t('plans.tally.completed')} value={String(total.completed)} />
        <Stat
          label={t('plans.tally.overdue')}
          value={String(total.overdue)}
          tone={total.overdue > 0 ? 'bad' : undefined}
        />
        <Stat
          label={t('plans.tally.missed')}
          value={String(total.missed)}
          tone={total.missed > 0 ? 'warn' : undefined}
        />
        <Stat
          label={t('plans.tally.rate')}
          value={total.rate !== null ? `%${fixed(total.rate, 1)}` : '—'}
        />
      </div>

      {report.byAuditor.length > 1 ? (
        <Card className="nv-tally">
          <h3>{t('plans.tally.byAuditor')}</h3>
          <table>
            <thead>
              <tr>
                <th>{t('plans.assignedTo')}</th>
                <th>{t('plans.tally.planned')}</th>
                <th>{t('plans.tally.completed')}</th>
                <th>{t('plans.tally.overdue')}</th>
                <th>{t('plans.tally.missed')}</th>
                <th>{t('plans.tally.rate')}</th>
              </tr>
            </thead>
            <tbody>
              {report.byAuditor.map((row) => (
                <tr key={row.name || '—'}>
                  <td>{row.name || '—'}</td>
                  <td>{row.planned}</td>
                  <td>{row.completed}</td>
                  <td className={row.overdue > 0 ? 'is-bad' : undefined}>{row.overdue}</td>
                  <td className={row.missed > 0 ? 'is-warn' : undefined}>{row.missed}</td>
                  <td>{row.rate !== null ? `%${fixed(row.rate, 1)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'bad' }) {
  return (
    <div className={tone ? `nv-mapstat nv-mapstat--${tone}` : 'nv-mapstat'}>
      <span className="nv-mapstat__label">{label}</span>
      <strong className="nv-mapstat__value">{value}</strong>
    </div>
  );
}
